import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, Spin } from 'antd'
import { Country } from '../../../types'

const { Option } = Select

interface CountrySelectProps {
  countries: Country[]
  selectedCountry: Country | null
  onCountrySelect: (country: Country | null) => void
  placeholder: string
  loading: boolean
  onDropdownVisibleChange?: (open: boolean) => void
  size?: 'small' | 'large' | 'default'
}

export const CountrySelect: FC<CountrySelectProps> = ({
  countries,
  selectedCountry,
  onCountrySelect,
  placeholder,
  loading,
  onDropdownVisibleChange,
  size = 'small',
}) => {
  const { t } = useTranslation()

  const handleChange = useCallback(
    (value: { key: string; label: string } | undefined) => {
      if (value && value.key) {
        const country = countries.find((country) => country === value.key)
        if (country) {
          onCountrySelect(country)
        }
      } else {
        onCountrySelect(null)
      }
    },
    [countries, onCountrySelect],
  )

  const notFoundContent = useMemo(() => {
    if (loading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
          <Spin size="small" style={{ marginRight: '8px' }} />
          <span>{t('navigation.search.loading')}</span>
        </div>
      )
    } else if (countries.length === 0) {
      return t('navigation.search.noResults')
    }
    return t('navigation.search.noResults')
  }, [countries.length, loading, t])

  return (
    <Select
      placeholder={placeholder}
      value={
        selectedCountry
          ? {
              key: selectedCountry,
              label: selectedCountry,
            }
          : undefined
      }
      labelInValue
      onChange={handleChange}
      allowClear
      size={size}
      loading={loading}
      notFoundContent={notFoundContent}
      style={{ width: '100%' }}
      dropdownMatchSelectWidth={false}
      onDropdownVisibleChange={onDropdownVisibleChange}
    >
      {countries
        .filter((country) => {
          // Filter out the currently selected country
          if (selectedCountry && country === selectedCountry) {
            return false
          }
          return true
        })
        .map((country) => (
          <Option key={country} value={country}>
            {country}
          </Option>
        ))}
    </Select>
  )
}
